import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';

import { Table, Column, Cell } from 'fixed-data-table-2';
import { Row, Col, Label, Button } from 'react-bootstrap';

import * as d3 from 'd3';
import {
  PieChart,
  Pie,
  Cell as PieCell,
  Legend,
  Tooltip as PieTip,
} from 'recharts';

import Tooltip from '../Tooltip';

import s from './Evidence.css';

const TermCell = ({ rowIndex, data, col, ...props }) => (
  <Cell {...props}>{data[rowIndex][col]}</Cell>
);

const TypeCell = ({ rowIndex, data, datatypeColors, col, ...props }) => {
  const color = datatypeColors[data[rowIndex].etype.title];
  return (
    <Cell {...props}>
      <Label
        style={{
          backgroundColor: color,
          border: `1px solid ${d3.rgb(color).darker(0.1)}`,
          borderRadius: '3px',
          color: 'white',
          fontSize: '11px',
        }}
      >
        <Tooltip text={data[rowIndex].etype.description}>
          {data[rowIndex].etype[col]}
        </Tooltip>
      </Label>
    </Cell>
  );
};

const color = d3
  .scaleLinear()
  .domain([-0.01, 0.4])
  .range(['#afafaf', '#3bafda']);

function colorCell(prob, text) {
  return (
    <div
      style={{
        width: `${100 * prob}%`,
        minHeight: 25,
        float: 'right',
        borderRadius: '2px 0 0 2px',
        background: color(prob),
      }}
    />
  );
}

const ProbCell = function({ rowIndex, data, col, ...props }) {
  const score = data[rowIndex][col];
  return (
    <Cell {...props}>
      <Row>
        <Col md={8}>{colorCell(score, () => score.toFixed(2))}</Col>
        <Col md={4} style={{ paddingLeft: 0 }}>
          <div
            style={{ height: 25, lineHeight: '25px', verticalAlign: 'middle' }}
          >
            {score < 0.01 ? '<.01' : score.toFixed(2)}
          </div>
        </Col>
      </Row>
    </Cell>
  );
};

/*
 * Component that displays dataset evidence corresponding to a
 * functional relationship edge
 */

const Evidence = ({
  title,
  networkTitle,
  datasets,
  datatypes,
  posterior,
  containerWidth,
}) => {
  // Build color mapping of datatype to color
  const datatypeColors = datatypes.reduce((o, v, i) => {
    o[v.title] = d3.scaleOrdinal(d3.schemeSet3).range()[i];
    return o;
  }, {});

  const colorScale = datatypes
    .filter(d => d.weight > 0)
    .map(d => datatypeColors[d.title]);

  // Build the data array for pie chart
  const data = datatypes
    .filter(d => d.weight > 0)
    .map(d => ({ name: d.title, value: Number(d.weight.toFixed(2)) }));

  return (
    <div className={s.root}>
      {title}
      <div style={{ height: '195px', position: 'relative' }}>
        <PieChart width={containerWidth} height={165} margin={{ bottom: -25 }}>
          <PieTip animationDuration={250} />
          <Legend height={20} />
          <Pie
            dataKey="value"
            data={data}
            animationBegin={0}
            animationDuration={800}
            innerRadius={45}
            outerRadius={80}
          >
            {data.map((entry, idx) => (
              <PieCell key={entry.name} fill={datatypeColors[entry.name]} />
            ))}
          </Pie>
        </PieChart>
        <div
          style={{
            position: 'absolute',
            top: '68px',
            left: containerWidth / 2 - 30,
          }}
        >
          <span style={{ fontSize: '27px' }}>{posterior.toFixed(2)}</span>
          <Tooltip
            placement="right"
            text={`The probability that the two genes functionally interact in ${networkTitle}`}
          />
        </div>
      </div>
      <div>
        <Table
          rowHeight={40}
          rowsCount={datasets.length}
          width={containerWidth}
          height={10 * 40 + 30}
          headerHeight={30}
        >
          <Column
            header={
              <Cell>
                CONTRIBUTION
                <Tooltip text="The contribution of a dataset to the prediction probability" />
              </Cell>
            }
            width={140}
            cell={<ProbCell data={datasets} col="posterior" />}
          />
          <Column
            header={<Cell>DATASET NAME</Cell>}
            width={160}
            cell={<TermCell data={datasets} col="title" />}
            flexGrow={1}
          />
          <Column
            header={<Cell>DATA TYPE</Cell>}
            width={120}
            cell={
              <TypeCell
                data={datasets}
                datatypeColors={datatypeColors}
                col="title"
              />
            }
          />
        </Table>
      </div>
    </div>
  );
};

Evidence.propTypes = {
  // Title to display
  title: PropTypes.object,

  networkTitle: PropTypes.string,

  // Array of datasets to display
  datasets: PropTypes.array.isRequired,

  // Array of datatypes represented by the datasets
  datatypes: PropTypes.array.isRequired,

  // Posterior probablity of the functional relationship
  posterior: PropTypes.number.isRequired,
};

Evidence.defaultProps = {
  networkTitle: '',
  containerWidth: 540,
};

export default withStyles(s)(Evidence);
